// ============================================================
// squareStatus — AI エージェントが Square の決済状況を読み、請求書の再発行ができるかを判断する（2026-10-31）
//
// きっかけ: TR-2TC95Y-3BBKHB「支払いができないので新しい請求書を送ってほしい」。請求書は FAILED、
//   決済の試みは6回すべて FAILED だった。前田さんはこの再発行を AI エージェント経由で行いたい。
//
// 取り方はサイドパネル（SquarePaymentInfo）と同じ:
//   GET {SYS_API_BASE}/payment/search_by_contact.php?contact_code=…&refresh=1&invoice_id=…
//   refresh=1 で Square に直接問い合わせ、invoice_id（予約の invoice_link）を渡すと
//   補償の支払い判定（coverage_payment.php）も返る。新しい Square クライアントは作らない。
//
// 認証: 決済 API はスタッフの Bearer 必須（sys/api/payment/config.php）。ここでは window.fetch をそのまま
//   使い、internalApiAuth が /sys/api/payment/ 宛てに Bearer を付ける（呼び出し側で鍵を持たない）。
//
// 金額の合計は utils/paymentStatus の computeSquareAmounts（予約一覧・サイドパネルと共通）を使う。
// ============================================================

import { SYS_API_BASE } from '../constants/calendar';
import { canOperateSquareIndex } from '../config/backend';
import { computeSquareAmounts } from '../utils/paymentStatus';
import { parseInvoiceLink } from '../components/editor2/hooks/useSquareInvoice';

/** Square の請求書の状態（スタッフ向けの言い方） */
export const SQUARE_INVOICE_STATUS_LABELS: Record<string, string> = {
  DRAFT: '下書き（お客様にはまだ届いていない）',
  UNPAID: '未払い（お客様に送付済み）',
  SCHEDULED: '送付予約',
  PARTIALLY_PAID: '一部だけ支払い済み',
  PAID: '支払い済み',
  PARTIALLY_REFUNDED: '一部返金済み',
  REFUNDED: '返金済み',
  CANCELED: '取り消し済み',
  FAILED: '支払い失敗（お客様はこの請求書では払えない）',
  PAYMENT_PENDING: '支払い処理中',
};

export function squareStatusLabel(status: string): string {
  return SQUARE_INVOICE_STATUS_LABELS[status] ?? (status ? `不明な状態（${status}）` : '不明');
}

/** 古い請求書をどう片付けるか */
export type VoidAction =
  /** square_void_invoice.php で Square 上の請求書を取り消す */
  | 'cancel'
  /** Square 上は既に使えない（FAILED / CANCELED）。Square は呼ばず、予約から外してメモに残す */
  | 'unlink'
  /** 請求書が無い。片付け不要 */
  | 'none';

export interface ReissueDecision {
  /** 再発行してよいか（false なら blocked_reason を案内して止まる） */
  allowed: boolean;
  void_action: VoidAction;
  blocked_reason?: string;
  note: string;
}

/**
 * 古い請求書の状態から、再発行してよいか・取り消しをどうするかを決める。
 * Square の CancelInvoice は DRAFT と終わった状態（PAID / REFUNDED / CANCELED / FAILED）を受け付けない。
 * square_void_invoice.php は PAID / PARTIALLY_PAID を取り消さずに「返金してください」のメールをスタッフへ送るだけなので、
 * 支払いのある請求書はここで止める（再発行すると二重請求になる）。
 */
export function decideReissue(invoiceLink: string, status: string | null): ReissueDecision {
  const link = String(invoiceLink ?? '').trim();
  if (!link) {
    return { allowed: true, void_action: 'none', note: '予約に請求書が結ばれていません。新しく作るだけです' };
  }
  const parsed = parseInvoiceLink(link);
  if (parsed.isPaymentUrl) {
    return { allowed: false, void_action: 'none', note: '',
      blocked_reason: '旧形式の決済リンク（square.link）で、Square 請求書として扱えません。Square の管理画面でスタッフが操作してください' };
  }
  if (!canOperateSquareIndex(parsed.sqNum)) {
    return { allowed: false, void_action: 'none', note: '',
      blocked_reason: `この請求書は他店舗の Square（sqNum=${parsed.sqNum}）で作られています。作った店舗の画面から操作してください` };
  }
  switch (status) {
    case 'UNPAID':
    case 'SCHEDULED':
      return { allowed: true, void_action: 'cancel',
        note: '古い請求書はまだ払える状態なので、先に Square で取り消してから新しく作ります（二重請求を防ぐ）' };
    case 'FAILED':
      // 2026-10-31 前田さん「経験がないので試してみます。失敗するようならオペレータが Square 上で取り消してから、API 経由で再度作成」。
      // 特別扱いせず今の取り消しの経路で試す。Square が受け付けなければ void_square_invoice が止めて案内を出す
      return { allowed: true, void_action: 'cancel',
        note: '古い請求書は支払い失敗です。Square で取り消しを試します（受け付けられなければ、Square の管理画面で取り消してから作成を指示してください）' };
    case 'CANCELED':
      return { allowed: true, void_action: 'unlink',
        note: '古い請求書は Square で取り消し済みです。予約から外して記録を残します' };
    case 'DRAFT':
      return { allowed: false, void_action: 'none', note: '',
        blocked_reason: '古い請求書は下書き（お客様に未送信）です。Square の取り消しは下書きに使えません。Square の管理画面で下書きを直して送るか、削除してから作り直してください（スタッフの操作）' };
    case 'PAID':
    case 'PARTIALLY_PAID':
    case 'PARTIALLY_REFUNDED':
    case 'REFUNDED':
      return { allowed: false, void_action: 'none', note: '',
        blocked_reason: `古い請求書は「${squareStatusLabel(status)}」です。支払いのある請求書は再発行しません（二重請求になる）。金額が変わったなら差額だけの請求をスタッフが Square で作ります` };
    case 'PAYMENT_PENDING':
      return { allowed: false, void_action: 'none', note: '',
        blocked_reason: '支払いの処理中です。結果が出るまで待ってから、もう一度状況を確かめてください' };
    default:
      return { allowed: false, void_action: 'none', note: '',
        blocked_reason: status
          ? `古い請求書の状態（${status}）が分からないため止めました。サイドパネルの Square 欄で確かめてください`
          : 'Square で古い請求書の状態を確かめられませんでした。時間をおいて状況の確認からやり直してください' };
  }
}

export interface SquareLineItem { name: string; quantity: string; amount: number }

export interface SquareInvoiceSummary {
  /** Square の請求書 ID（inv:0-…）。予約の invoice_link は sq:{店舗}:{この ID} */
  invoice_id: string;
  /** Square の請求書番号（お客様の請求書に出る番号） */
  invoice_number: string | null;
  invoice_link: string | null;
  linked: boolean;
  status: string;
  status_label: string;
  amount: number;
  paid_amount: number;
  delivery_method: string | null;
  created_at: string | null;
  order_state: string | null;
  line_items: SquareLineItem[];
}

export interface SquareStatusSummary {
  ok: boolean;
  error?: string;
  /** 予約に結ばれている請求書（無ければ null） */
  invoice: SquareInvoiceSummary | null;
  /** 同じ予約番号の、結ばれていない請求書（過去に作り直したもの等） */
  other_invoices: SquareInvoiceSummary[];
  payment_attempts: { total: number; completed: number; failed: number; other: number; last_at: string | null; last_status: string | null };
  refunds: { count: number; amount: number };
  totals: { paid: number; refunded: number; net: number };
  coverage_payment: unknown;
  reissue: ReissueDecision;
  warnings?: string[];
}

function lineItemsOf(order: any): SquareLineItem[] {
  const items = Array.isArray(order?.line_items) ? order.line_items : [];
  return items.map((li: any) => ({
    name: String(li?.name ?? ''),
    quantity: String(li?.quantity ?? '1'),
    // DB 由来は Square の形（total_money.amount）、API fallback 由来は total に平たくなっている
    amount: Number(li?.total_money?.amount ?? li?.total ?? 0) || 0,
  }));
}

/** search_by_contact.php のレスポンスを、AI に渡す形にまとめる（カード番号・お客様のメールは入れない） */
export function summarizeSquareStatus(resp: any, invoiceLink: string): SquareStatusSummary {
  const link = String(invoiceLink ?? '').trim();
  const empty = {
    invoice: null, other_invoices: [],
    payment_attempts: { total: 0, completed: 0, failed: 0, other: 0, last_at: null, last_status: null },
    refunds: { count: 0, amount: 0 }, totals: { paid: 0, refunded: 0, net: 0 }, coverage_payment: null,
  };
  if (!resp || resp.status !== 'ok') {
    const error = String(resp?.error ?? resp?.message ?? '決済 API から読めませんでした');
    return { ok: false, error, ...empty, reissue: decideReissue(link, null) };
  }
  const invoices: any[] = Array.isArray(resp.invoices) ? resp.invoices : [];
  const orders: any[] = Array.isArray(resp.orders) ? resp.orders : [];
  const payments: any[] = Array.isArray(resp.payments) ? resp.payments : [];
  const refunds: any[] = Array.isArray(resp.refunds) ? resp.refunds : [];
  const linkedId = link ? parseInvoiceLink(link).actualId : '';

  const toSummary = (inv: any): SquareInvoiceSummary => {
    const order = orders.find((o) => o.order_id === inv.order_id);
    const isLinked = !!linkedId && inv.invoice_id === linkedId;
    return {
      invoice_id: String(inv.invoice_id ?? ''),
      invoice_number: inv.invoice_number != null && inv.invoice_number !== '' ? String(inv.invoice_number) : null,
      invoice_link: isLinked ? link : null,
      linked: isLinked,
      status: String(inv.status ?? ''),
      status_label: squareStatusLabel(String(inv.status ?? '')),
      amount: Number(inv.computed_amount) || 0,
      paid_amount: Number(inv.total_completed_amount) || 0,
      delivery_method: inv.delivery_method ?? null,
      created_at: inv.created_at ?? null,
      order_state: order?.state ?? null,
      line_items: lineItemsOf(order),
    };
  };
  const all = invoices.map(toSummary);
  const linked = all.find((x) => x.linked) ?? null;

  // 決済の試みは結ばれた請求書の order に絞る（無ければ全部）
  const linkedOrderId = linked ? invoices.find((i) => i.invoice_id === linkedId)?.order_id : null;
  const pays = linkedOrderId ? payments.filter((p) => p.order_id === linkedOrderId) : payments;
  const sorted = [...pays].sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));
  const completed = pays.filter((p) => p.status === 'COMPLETED' || p.status === 'APPROVED').length;
  const failed = pays.filter((p) => p.status === 'FAILED' || p.status === 'CANCELED').length;
  const amounts = computeSquareAmounts({ invoices, payments, orders, refunds, fetchedAt: 0 } as any);

  return {
    ok: true,
    invoice: linked,
    other_invoices: all.filter((x) => !x.linked),
    payment_attempts: {
      total: pays.length, completed, failed, other: pays.length - completed - failed,
      last_at: sorted[0]?.created_at ?? null, last_status: sorted[0]?.status ?? null,
    },
    refunds: { count: refunds.length, amount: amounts.refundAmount },
    totals: { paid: amounts.paidAmount, refunded: amounts.refundAmount, net: amounts.netAmount },
    coverage_payment: resp.coverage_payment ?? null,
    // 結ばれた請求書が決済 DB に見つからない＝状態が分からない（null）→ decideReissue が止める
    reissue: decideReissue(link, linked ? linked.status : null),
    ...(Array.isArray(resp.warnings) && resp.warnings.length ? { warnings: resp.warnings.map(String) } : {}),
  };
}

/** サイドパネルと同じ URL（Square に直接問い合わせる） */
export function squareStatusUrl(contactCode: string, invoiceLink: string): string {
  return `${SYS_API_BASE}/payment/search_by_contact.php?contact_code=${encodeURIComponent(contactCode)}&refresh=1`
    + (invoiceLink ? `&invoice_id=${encodeURIComponent(invoiceLink)}` : '');
}

/**
 * 決済 API から読んでまとめる。fetch は window.fetch（internalApiAuth が Bearer を付ける）。
 * 401/403 は「ログインし直し」を返す（鍵なしで動く前提にしない）。
 */
export async function fetchSquareStatus(contactCode: string, invoiceLink: string): Promise<SquareStatusSummary> {
  let res: Response;
  try {
    res = await fetch(squareStatusUrl(contactCode, invoiceLink));
  } catch (e: any) {
    return summarizeSquareStatus({ status: 'error', error: `決済 API に接続できませんでした（${e?.message || e}）` }, invoiceLink);
  }
  if (res.status === 401 || res.status === 403) {
    return summarizeSquareStatus({ status: 'error',
      error: `決済 API の認証に失敗しました（HTTP ${res.status}）。ログインし直してからもう一度お試しください` }, invoiceLink);
  }
  const json = await res.json().catch(() => null);
  if (!res.ok && json?.status !== 'ok') {
    return summarizeSquareStatus({ status: 'error', error: json?.error ?? `HTTP ${res.status}` }, invoiceLink);
  }
  return summarizeSquareStatus(json, invoiceLink);
}

/** 明細が同じか（名前・数量・金額の組を順不同で比べる）。違う行を返す */
export function diffLineItems(oldItems: SquareLineItem[], newItems: SquareLineItem[]): { same: boolean; only_old: SquareLineItem[]; only_new: SquareLineItem[] } {
  const key = (l: SquareLineItem) => `${l.name}|${l.quantity}|${l.amount}`;
  const rest = [...newItems];
  const onlyOld: SquareLineItem[] = [];
  for (const o of oldItems) {
    const i = rest.findIndex((n) => key(n) === key(o));
    if (i >= 0) rest.splice(i, 1); else onlyOld.push(o);
  }
  return { same: onlyOld.length === 0 && rest.length === 0, only_old: onlyOld, only_new: rest };
}

/**
 * 作り直しの手本にする古い請求書: 予約に結ばれた FAILED / CANCELED、無ければ同じ予約番号の
 * FAILED / CANCELED のうち一番新しいもの（void_square_invoice で外した直後はこちら）
 */
export function referenceInvoice(summary: SquareStatusSummary): SquareInvoiceSummary | null {
  const dead = (x: SquareInvoiceSummary | null) => !!x && (x.status === 'FAILED' || x.status === 'CANCELED');
  if (dead(summary.invoice)) return summary.invoice;
  const others = summary.other_invoices.filter(dead)
    .sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));
  return others[0] ?? null;
}

/**
 * 新しい請求書を作ってよいか（予約に結ばれた古い請求書に対して）。
 * 取り消し済み（CANCELED）か、終わった状態で未払い（FAILED・支払い 0）なら、取り消しの手順は済んだものとして作ってよい
 * （2026-10-31: オペレータが Square の管理画面で取り消してから「再度作成します」と指示する流れ）。
 */
export function canCreateOver(link: string, inv: SquareInvoiceSummary | null): { ok: boolean; reason?: string } {
  if (!String(link ?? '').trim()) return { ok: true };
  // 旧形式の決済リンク・他店舗の請求書は decideReissue と同じ理由で止める
  const linkCheck = decideReissue(link, 'CANCELED');
  if (!linkCheck.allowed) return { ok: false, reason: linkCheck.blocked_reason };
  if (!inv) return { ok: false, reason: 'Square で古い請求書の状態を確かめられませんでした。時間をおいて状況の確認からやり直してください' };
  if ((inv.status === 'CANCELED' || inv.status === 'FAILED') && inv.paid_amount === 0) return { ok: true };
  if (inv.status === 'UNPAID' || inv.status === 'SCHEDULED') {
    return { ok: false, reason: `まだ払える請求書（${inv.status}・¥${inv.amount.toLocaleString()}）が結ばれています。先に void_square_invoice で取り消してください` };
  }
  return { ok: false, reason: decideReissue(link, inv.status).blocked_reason ?? `古い請求書の状態（${inv.status}）では作れません` };
}

/** Square のエラー（"Square API Error: [{...}]"）を、人が読める1行にする */
export function readableSquareError(raw: unknown): string {
  const text = String(raw ?? '').trim();
  const m = text.match(/\[[\s\S]*\]/);
  if (m) {
    try {
      const errs = JSON.parse(m[0]);
      if (Array.isArray(errs) && errs.length) {
        return errs.map((e: any) => [e?.detail, e?.code ? `（${e.code}）` : ''].filter(Boolean).join('')).join(' / ');
      }
    } catch { /* JSON でなければそのまま返す */ }
  }
  return text || '応答なし';
}

/** Square の管理画面でこの請求書を開く URL（サイドパネルの［Squareで開く］と同じ） */
export function squareDashboardUrl(link: string): string | null {
  const p = parseInvoiceLink(String(link ?? '').trim());
  if (!p.actualId || p.isPaymentUrl) return null;
  return `https://app.squareup.com/dashboard/invoices/${p.actualId}/edit?currentUnitToken=${p.locationToken}`;
}
